/**
 * Emoji typed into text fields.
 *
 * A shopper's phone shows its own emoji (Apple's, Samsung's, Google's), but a
 * print can only carry one set, and Apple's may not be printed at all. So every
 * emoji is drawn in Noto Color Emoji (OFL, vector COLRv1): the same font in the
 * shop preview, in Studio and in the print file, so the preview shows exactly
 * the emoji that will be printed. A layer that prints in one colour (engraving,
 * laser, embroidery) takes the monochrome Noto Emoji instead, filled like its
 * letters (`TextElement.emoji = 'mono'`).
 *
 * The two fonts are not shop fonts: they ship with the app and are addressed
 * by the reserved ids below, which the renderer loads on its own whenever a
 * text needs them — a name without emoji never downloads them.
 *
 * Every typed field accepts emoji unless it says otherwise
 * (`config.allowEmojis: false`, see {@link emojiAllowed}); a field that does not
 * is filtered by {@link stripEmoji} on the form and checked again on the server.
 */

/**
 * Whether a typed field takes emoji: yes, unless the field turns them off with
 * `allowEmojis: false`. Every emoji the bundled fonts carry prints, so the
 * owner's rule is "always, if we have it"; the 165 fields imported from
 * Customily carry no setting at all and take them too.
 */
export function emojiAllowed(config: { allowEmojis?: unknown } | null | undefined): boolean {
  return config?.allowEmojis !== false;
}

export type EmojiStyle = 'color' | 'mono';

export const EMOJI_STYLES: readonly EmojiStyle[] = ['color', 'mono'];

/** Reserved font ids of the bundled emoji fonts; never a shop font's id (those are uuids). */
export const EMOJI_FONT_IDS: Readonly<Record<EmojiStyle, string>> = {
  color: '@emoji/color',
  mono: '@emoji/mono',
};

/**
 * The names the API serves the emoji fonts under, beside the widget bundle
 * (`/widget/emoji-color.woff2`) — where a browser fetches them from.
 */
export const EMOJI_FONT_WEB_FILES: Readonly<Record<EmojiStyle, string>> = {
  color: 'emoji-color.woff2',
  mono: 'emoji-mono.woff2',
};

/** The style an emoji font id stands for, or undefined for any other font. */
export function emojiStyleOfFontId(fontId: string): EmojiStyle | undefined {
  if (fontId === EMOJI_FONT_IDS.color) return 'color';
  if (fontId === EMOJI_FONT_IDS.mono) return 'mono';
  return undefined;
}

/** How a text layer draws its emoji: in colour unless it says monochrome. */
export function emojiStyleOf(el: { emoji?: EmojiStyle }): EmojiStyle {
  return el.emoji === 'mono' ? 'mono' : 'color';
}

/** The bundled emoji font a text layer falls back to. */
export function emojiFontIdOf(el: { emoji?: EmojiStyle }): string {
  return EMOJI_FONT_IDS[emojiStyleOf(el)];
}

/**
 * User-perceived characters. A family emoji is one character made of five code
 * points (eleven UTF-16 units); counting it as eleven letters would charge the
 * shopper for eleven and let a limit cut it in half.
 */
export function textGraphemes(text: string): string[] {
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text), (s) => s.segment);
  }
  return [...text];
}

/** How many characters a shopper sees in a text: graphemes, not UTF-16 units. */
export function textLength(text: string): number {
  return textGraphemes(text).length;
}

/** The first `max` characters of a text, never splitting an emoji or an accent. */
export function truncateText(text: string, max: number): string {
  if (max < 0) return '';
  const chars = textGraphemes(text);
  return chars.length <= max ? text : chars.slice(0, max).join('');
}

/** Characters a price per letter counts: every grapheme but blanks. */
export function countedLetters(text: string): number {
  return textGraphemes(text).filter((g) => !/^\s+$/u.test(g)).length;
}

/**
 * A grapheme the shopper sees as an emoji rather than a letter or symbol.
 *
 * Shown as emoji by default (😀, 🏽), forced to emoji by U+FE0F (❤️), a flag
 * (two regional indicators, or a tag sequence 🏴󠁧󠁢󠁳󠁣󠁴󠁿) or a keycap (1️⃣).
 * Symbols that are text by default — ♥ © ® ™ ★ — stay letters: "Ana ♥ Marko"
 * is ordinary text a field without emoji still accepts. Decided by the
 * grapheme's FIRST character: a skin tone typed after a letter joins that
 * letter's grapheme, and the letter is not an emoji.
 */
export function isEmojiGrapheme(grapheme: string): boolean {
  const first = String.fromCodePoint(grapheme.codePointAt(0) ?? 0x20);
  if (/\p{Emoji_Presentation}|\p{Emoji_Modifier}|\p{Regional_Indicator}/u.test(first)) return true;
  if (/\p{Extended_Pictographic}/u.test(first)) return grapheme.includes('️');
  return /^[#*0-9]$/.test(first) && grapheme.includes('⃣');
}

/** Anything that could start an emoji; a quick test before segmenting. */
const MAYBE_EMOJI = /\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|⃣/u;

/** True when a text contains at least one emoji. */
export function hasEmoji(text: string): boolean {
  return MAYBE_EMOJI.test(text) && textGraphemes(text).some(isEmojiGrapheme);
}

/**
 * True when a text may need the emoji font to draw: an emoji, or a pictographic
 * symbol (♥ ✈ ☎) a shop font often lacks. Broader than {@link hasEmoji} so a ♥
 * the font has no glyph for is drawn from the emoji font rather than as a box.
 */
const PICTOGRAPHIC = /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u;

export function needsEmojiFont(text: string): boolean {
  return PICTOGRAPHIC.test(text);
}

/** Emoji parts left behind on their own: joiners, selectors, skin tones, tags, keycap marks. */
const EMOJI_LEFTOVERS = /[‍︎️⃣\u{1F3FB}-\u{1F3FF}\u{E0020}-\u{E007F}]/gu;
const HAS_LEFTOVERS = new RegExp(EMOJI_LEFTOVERS.source, 'u');

/**
 * The text without its emoji, for a field that does not accept them. Removes
 * whole emoji — skin tones, flags and keycaps included — and keeps text
 * symbols (♥ © ® ™ ★) and every letter.
 */
export function stripEmoji(text: string): string {
  if (!hasEmoji(text) && !HAS_LEFTOVERS.test(text)) return text;
  return textGraphemes(text)
    .filter((g) => !isEmojiGrapheme(g))
    .map((g) => g.replace(EMOJI_LEFTOVERS, ''))
    .join('');
}

/**
 * The text without unpaired surrogates — half an emoji, left by a cut that
 * counted UTF-16 units. JSON carries it as `\ud83d`, which Postgres refuses in
 * a jsonb column, so one stray half would fail the whole order.
 */
export function dropLoneSurrogates(text: string): string {
  return text.replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '');
}
